import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import { pageOf } from '@/auth/rolePages';
import { RETURN_URL_KEY, RETURN_URL_OWNER_KEY } from '@/auth/roleState';
import { isSigningOut } from '@/config/signOutState';

export { RETURN_URL_KEY };

/**
 * Keeps the address of a page that was opened without a session (a bookmark, a link from an
 * e-mail, a new tab after the session ended), so the sign-in that follows returns to it.
 *
 * The session lives in memory, so such a page load finds nobody signed in and the route guard
 * sends it to the login page before the role's page could be noted (auth/RolePageRecorder.tsx
 * writes `sessionurl` only while a role is active). Without this the next sign-in returns to
 * the home page, or to the page that was open when the previous session ended.
 *
 * A deep link has no owner (`sessionurl_ra` is removed): whichever role the sign-in chooses
 * opens it.
 *
 * Not kept:
 * - during a sign-out of this tab (the user chose to leave; an expired session keeps the page
 *   the role noted, see auth/clearSession.ts `expired`);
 * - the login pages and the no-permission / no-access pages.
 *
 * Only this app's own address is stored; the page it names is opened through the router and
 * its gates after the sign-in, and the server decides what the signed-in user may see.
 * Returns true when the address was kept.
 */
export function rememberReturnUrl(href: string = window.location.href): boolean {
  if (isSigningOut()) return false;
  if (href.indexOf('#/') < 0 || pageOf(href) === null) return false;
  try {
    localStorage.setItem(RETURN_URL_KEY, href);
    localStorage.removeItem(RETURN_URL_OWNER_KEY);
    return true;
  } catch {
    return false; // storage unavailable: the sign-in lands on the home page
  }
}

/**
 * What a signed-in route renders when nobody is signed in: the address is kept for the next
 * sign-in, then the login page.
 */
export const SignedOutRedirect = () => {
  // once, while this mounts: <Navigate> changes the address in its own effect
  useState(() => rememberReturnUrl());
  return <Navigate to='/' />;
};
